
// ResizableVertical.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';

const ResizableVertical = ({
  initialHeight = 200,   // 200 | "200" | "40%"
  minHeight = 100,       // 100 | "100" | "10%"
  maxHeight = 600,       // 600 | "600" | "90%"
  children,
}) => {
  const wrapperRef = useRef(null);
  const isResizingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const [height, setHeight] = useState(0);

  const resolveValue = (value, containerHeight) => {
    if (typeof value === 'number') return value;

    if (typeof value === 'string') {
      const trimmed = value.trim();

      // porcentaje
      if (trimmed.endsWith('%') && containerHeight) {
        const num = parseFloat(trimmed);
        if (!isNaN(num)) {
          return (num / 100) * containerHeight;
        }
      }

      // número en string ("400")
      const num = parseFloat(trimmed);
      if (!isNaN(num)) return num;
    }

    return 0;
  };

  const clamp = useCallback(
    (heightPx) => {
      const parent = wrapperRef.current?.parentElement;
      if (!parent) return heightPx;

      const { height: containerHeight } = parent.getBoundingClientRect();

      const minPx = resolveValue(minHeight, containerHeight);
      const maxPx = resolveValue(maxHeight, containerHeight);

      return Math.min(Math.max(heightPx, minPx), maxPx);
    },
    [minHeight, maxHeight]
  );

  // Altura inicial (SOLO una vez / cuando cambian props)
  useEffect(() => {
    const parent = wrapperRef.current?.parentElement;
    if (!parent) return;

    const { height: containerHeight } = parent.getBoundingClientRect();
    const target = resolveValue(initialHeight, containerHeight);

    setHeight(clamp(target));
  }, [initialHeight, clamp]);

  // Drag vertical
  useEffect(() => {
    const handleMove = (e) => {
      if (!isResizingRef.current) return;

      const delta = e.clientY - startYRef.current;
      // arrastrar hacia arriba disminuye la altura del footer
      const newHeight = startHeightRef.current - delta;

      setHeight((prev) => clamp(newHeight));
    };

    const handleUp = () => {
      isResizingRef.current = false;
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);

    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [clamp]);

  const handleDown = (e) => {
    isResizingRef.current = true;
    startYRef.current = e.clientY;
    startHeightRef.current = height;
  };

  const child = React.Children.only(children);

  return (
    <div
      ref={wrapperRef}
      style={{
        position: 'relative',
        height: `${height}px`,
        flex: `0 0 ${height}px`,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Handler ARRIBA: entre Content y Footer */}
      <div
        onMouseDown={handleDown}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          height: 6,
          width: '100%',
          cursor: 'row-resize',
          userSelect: 'none',
          zIndex: 10,
          // ponle color si quieres verlo mejor:
          // background: '#e5e7eb',
        }}
      />

      {React.cloneElement(child, {
        style: {
          ...(child.props.style || {}),
          height: '100%',
          width: '100%',
          marginTop: 6, // deja sitio al handler por dentro
        },
      })}
    </div>
  );
};

export default ResizableVertical;
